import React, { useEffect, useCallback } from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, fetchUserAttributes,signOut } from 'aws-amplify/auth';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { jwtDecode } from 'jwt-decode';
import { checkAuthStatus } from '../../services/AdminApis'; // No changes needed in AdminApis.js
import awsmobile from '../../aws-exports'; // Import your Amplify config

const POST_LOGIN_REDIRECT_KEY = 'selyst_post_login_redirect';
const DEFAULT_POST_LOGIN_PATH = '/admin/search';

const getPostLoginRedirectPath = () => {
  const savedPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

  if (savedPath?.startsWith('/admin')) {
    return savedPath;
  }

  return DEFAULT_POST_LOGIN_PATH;
};

const OAuthCallback = () => {
  const { loginFromToken } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSuccessfulSignIn = useCallback(async () => {
    try {
      // 1. Get initial session from Amplify
      let session = await fetchAuthSession();
      console.log("Full session object from Amplify:", session);
      let idToken = session.tokens?.idToken?.toString();
      let accessToken = session.tokens?.accessToken?.toString();
      //let refreshToken = session.tokens?.refreshToken?.toString();

      console.log("idToken:", idToken);
      console.log("accessToken:", accessToken);
      

      if (!idToken || !accessToken) {
        throw new Error("Initial authentication tokens not found.");
      }

      // --- START: NEW CODE TO GET REFRESH TOKEN ---
      const idTokenPayload = jwtDecode(idToken);
      const { userPoolClientId } = awsmobile.Auth.Cognito;
      const cognitoUsername = idTokenPayload['cognito:username'];
      console.log("userPoolClientId:", userPoolClientId);
      console.log("cognitoUsername:", cognitoUsername);

      const refreshTokenKey = `CognitoIdentityServiceProvider.${userPoolClientId}.${cognitoUsername}.refreshToken`;
      console.log("refreshTokenKey:", refreshTokenKey);
      let refreshToken = localStorage.getItem(refreshTokenKey);

      if (!refreshToken) {
        throw new Error("Refresh token could not be retrieved from storage.");
      }
      // --- END: NEW CODE ---
      console.log("refreshToken:", refreshToken);

      const cognitoUserAttributes = await fetchUserAttributes();
      let userInfo = {
        username: cognitoUserAttributes.sub,
        email: cognitoUserAttributes.email,
        groups: (jwtDecode(idToken)['cognito:groups']) || [],
      };

      // 2. Configure the AuthContext and API service immediately
      await loginFromToken(idToken, accessToken, refreshToken, userInfo);

      // 3. Now check if a group sync is needed
      if (userInfo.groups.length === 0) {
        toast({
          title: "First-time sign-in",
          description: "Syncing account permissions...",
        });
        
        // 4. Call checkAuthStatus. It will now use the token configured in the AuthContext.
        await checkAuthStatus();
        
        // 5. Refresh the session to get a NEW token containing the updated group claim
        session = await fetchAuthSession({ forceRefresh: true });
        idToken = session.tokens?.idToken?.toString();
        accessToken = session.tokens?.accessToken?.toString();
        refreshToken = session.tokens?.refreshToken?.toString();

        if (!idToken) throw new Error("Failed to refresh session after group sync.");
        
        // 6. Update the AuthContext AGAIN with the final, correct user info
        userInfo.groups = (jwtDecode(idToken)['cognito:groups']) || [];
        await loginFromToken(idToken, accessToken, refreshToken, userInfo);
      }

      toast({ title: "Sign-In Successful", description: "Welcome!" });
      navigate(getPostLoginRedirectPath(), { replace: true });

    } catch (error) {
      // --- 2. This 'catch' block is updated ---
      console.error("Authentication or Provisioning Failed:", error);

      // Determine the error message
      const errorMessage = error.response?.data?.detail || // For backend errors
                         error.message ||                    // For frontend errors
                         "An unknown error occurred.";
      toast({ 
        variant: "destructive", 
        title: "Access Denied", 
        description: errorMessage,
      });

      // Log the user out of the Cognito session completely
      try {
        await signOut();
        console.log("User successfully signed out of Cognito session.");
      } catch (signOutError) {
        console.error("Error during sign out:", signOutError);
      }
      
      // Redirect to the login page
      navigate('/');
    }
  }, [loginFromToken, navigate, toast]);

  // The useEffect with Hub.listen remains the same
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        handleSuccessfulSignIn();
      } else if (payload.event === 'signInWithRedirect_failure') {
        console.error("Hub event: signInWithRedirect failed.", payload.data);
        toast({ variant: "destructive", title: "Sign-In Failed", description: "There was a problem during the sign-in process." });
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [handleSuccessfulSignIn, navigate, toast]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <p>Processing sign-in...</p>
    </div>
  );
};

export default OAuthCallback;
